import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { apiCall } from '../_shared';
import RateWatchCard from '../RateWatchCard';

const SYNC = {
  match: ['In sync', 'bg-emerald-100 text-emerald-800'],
  differs: ['Rate differs', 'bg-red-100 text-red-800'],
  missing: ['Not on Booking.com', 'bg-amber-100 text-amber-900'],
  no_direct: ['No Calendar rate', 'bg-slate-100 text-slate-600'],
};
const eur = (n) => (n == null ? '—' : `€${Number(n).toFixed(0)}`);

// Nightly Booking.com page read vs the Calendar (Beds24) price for a sample stay — flags rooms whose rate did not arrive or differs.
export const RateSyncPanel = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(() => apiCall('/api/admin/rate-watch').then(setData).catch((e) => setErr(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const run = async () => { setBusy(true); setErr(''); try { await apiCall('/api/admin/rate-watch/run', { method: 'POST' }); await load(); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  const L = data?.latest;
  const rows = (L?.rows || []).map((r) => {
    const s = SYNC[r.sync || (r.booking_per_night == null ? 'missing' : 'match')] || SYNC.match;
    return (
      <tr key={r.room_id} className={`border-t ${r.sync === 'differs' ? 'bg-red-50' : r.sync === 'missing' ? 'bg-amber-50' : ''}`} data-testid={`rs-row-${r.room_id}`}>
        <td className="py-1.5 pr-3 font-semibold text-slate-900">{r.room_name}</td>
        <td className="py-1.5 pr-3 text-right">{eur(r.direct_per_night)}</td>
        <td className="py-1.5 pr-3 text-right">{eur(r.booking_per_night)}</td>
        <td className="py-1.5 pr-3 text-right">{r.diff_pct == null ? '—' : `${r.diff_pct > 0 ? '+' : ''}${r.diff_pct} %`}</td>
        <td className="py-1.5"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${s[1]}`} data-testid={`rs-sync-${r.room_id}`}>{s[0]}</span></td>
      </tr>
    );
  });
  const out = L?.out_of_sync;
  return (
    <div className="space-y-4" data-testid="rates-sync">
      <div className="bg-white rounded-xl border p-5">
        <div className="flex flex-wrap items-start gap-3">
          <div className="flex-1 min-w-0">
            <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#003b95]" /> Rate sync check</h3>
            <p className="text-xs text-slate-600 mt-1">Every night the public Booking.com page is read for the sample stay{data?.next_stay ? ` (${data.next_stay.check_in} → ${data.next_stay.check_out})` : ''} and each room's price is compared with the Calendar rate from Beds24. Rooms whose rate did not arrive, or differs by more than {data?.settings?.threshold_pct ?? 2} %, are flagged here and in the manager push/e-mail.</p>
          </div>
          <button type="button" onClick={run} disabled={busy} className="bg-[#0071c2] text-white rounded px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="rs-run">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Check now</button>
        </div>
        {err && <p className="text-xs text-red-700 mt-2" data-testid="rs-error">{err}</p>}
        {!data && !err && <p className="text-xs text-slate-500 mt-3">Loading…</p>}
        {data && !L && <p className="text-xs text-slate-500 mt-3" data-testid="rs-empty">No check yet — press "Check now".</p>}
        {L && (
          <div className="mt-3">
            <div className="flex flex-wrap items-center gap-2 text-xs mb-2">
              {L.status !== 'ok' && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 font-semibold" data-testid="rs-status">{L.status === 'no_rooms' ? 'Booking.com shows no rooms for these dates (property closed or sold out) — nothing to compare' : `Check status: ${L.status}`}</span>}
              {L.status === 'ok' && (out ? <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-800 font-semibold" data-testid="rs-flag">⚠ {out} room(s) out of sync</span> : <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-semibold" data-testid="rs-flag">All matched rooms in sync ✓</span>)}
              <span className="text-slate-500">last check {L.at.slice(0, 16).replace('T', ' ')} UTC · {L.check_in} → {L.check_out}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="rs-table">
                <thead><tr className="text-left text-[10px] uppercase text-slate-500"><th className="pb-1 pr-3">Room</th><th className="pb-1 pr-3 text-right">Calendar (Beds24) / night</th><th className="pb-1 pr-3 text-right">Booking.com / night</th><th className="pb-1 pr-3 text-right">Diff</th><th className="pb-1">Status</th></tr></thead>
                <tbody>{rows}</tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      <RateWatchCard />
    </div>
  );
};
